/**
 * Pure presentation/telemetry helpers for edge bots — the queue-distance heat
 * ramp, posture tone, queue math, climb/ETA motion derivation, and formatters.
 * Extracted from the Recycler page so the Position Tracker bot zones reuse the
 * exact same intelligence + visual vocabulary. No React, no I/O.
 */
import type { AgentStats } from './edge';

export const FRONT_AT = 20; // <= contracts ahead → at the front (fill / adverse-selection risk)
export const FRONT_NEAR = 50; // <= contracts ahead → near the front
export const QUEUE_SCALE = 300; // contracts reference for the fill anchor (full = at the front)
export const POLL_MS = 1000;
export const HIST = 24; // poll samples kept per agent for climb / ETA / sparkline

export type ViewMode = 'compact' | 'card' | 'table';
export type SortKey = 'front' | 'eta' | 'net' | 'errors';
export type Sample = { ms: number; front: number };
/** `prox` = the queue ahead NOW as a % of where it was at placement (the start of
 *  the current contiguous resting run) — the readout for the queuerecycler's
 *  `proximityRatioPct` knob: as the line advances toward the front it falls from
 *  100 toward the knob's trigger. -1 = not derivable yet (no run / placement was
 *  already at the front). */
export type Motion = { climb: number; eta: number; prox: number; spark: number[] };

// ── queue-distance heat: the ONLY rainbow axis. RED far from the front → GREEN
//    as the line nears fill. `rgb` is the raw triplet for full-row gradients.
export function heat(ahead: number): { text: string; fill: string; bar: string; rgb: string } {
  if (ahead < 0) return { text: 'text-muted-foreground', fill: 'bg-muted-foreground/10', bar: 'bg-muted-foreground/50', rgb: '148 163 184' };
  if (ahead <= FRONT_AT) return { text: 'text-green-400', fill: 'bg-green-500/12', bar: 'bg-green-500', rgb: '34 197 94' };
  if (ahead <= FRONT_NEAR) return { text: 'text-amber-400', fill: 'bg-amber-500/12', bar: 'bg-amber-500', rgb: '245 158 11' };
  return { text: 'text-red-400', fill: 'bg-red-500/12', bar: 'bg-red-500', rgb: '239 68 68' };
}

// ── posture: bot health, a SEPARATE channel (dot + pill, never the heat ramp).
export function postureTone(p: string): { dot: string; cls: string; label: string } {
  switch (p) {
    case 'green': return { dot: 'bg-green-500', cls: 'text-green-400', label: 'healthy' };
    case 'yellow': return { dot: 'bg-amber-500', cls: 'text-amber-400', label: 'working' };
    case 'orange': return { dot: 'bg-orange-500', cls: 'text-orange-400', label: 'strained' };
    case 'red': return { dot: 'bg-red-500', cls: 'text-red-400', label: 'trouble' };
    default: return { dot: 'bg-muted-foreground', cls: 'text-muted-foreground', label: p || '—' };
  }
}

export function frontOf(a: AgentStats): number {
  const r = (a.resting ?? []).map((o) => o.queuePosition).filter((q) => q >= 0);
  return r.length ? Math.min(...r) : -1;
}

/** Whether to surface the front-of-book metrics (front / climb / fill-ETA — all
 *  derived from queuePosition) for this bot RIGHT NOW. DATA-driven, not kind-
 *  driven: the edge enriches queuePosition for every resting kind (incl. vanilla
 *  MM, via the g10edge runner depth enrichment), so we show the cells whenever a
 *  live queue position is present and fall back to "—" (feed down / between
 *  cycles / no resting leg) when it isn't. The earlier /recycl/ kind gate WRONGLY
 *  hid a live MM bot's real queue position — the edge had been emitting it all
 *  along — which is exactly the visibility Josh wanted on the running MM bot. */
export function hasLiveQueue(a: AgentStats): boolean {
  return frontOf(a) >= 0;
}

export function depthSource(a: AgentStats): string {
  const direct = (a.depthSource ?? '').trim();
  if (direct) return direct;
  const sources = new Set((a.resting ?? []).filter((o) => o.queuePosition >= 0).map((o) => o.queueSource).filter(Boolean));
  if (sources.size === 0) return 'none';
  if (sources.size > 1) return 'mixed';
  return sources.values().next().value ?? 'none';
}

export function sourceTone(source: string): string {
  switch (source) {
    case 'direct': return 'text-green-400';
    case 'hydra': return 'text-amber-400';
    case 'mixed': return 'text-sky-400';
    case 'orders': return 'text-muted-foreground';
    default: return 'text-muted-foreground/50';
  }
}

export function queueState(a: AgentStats): { label: string; cls: string } {
  if (a.haltReason) {
    return { label: queueReasonLabel(a.haltReason), cls: 'text-red-400' };
  }
  if (hasLiveQueue(a)) {
    const src = depthSource(a);
    return { label: src === 'none' ? 'live' : src, cls: sourceTone(src) };
  }
  switch (a.queueStatus) {
    case 'suppressed':
      return { label: queueReasonLabel(a.queueStatusReason), cls: 'text-amber-400' };
    case 'unknown_queue':
      return { label: 'unknown', cls: 'text-muted-foreground/60' };
    case 'placing':
      return { label: 'placing', cls: 'text-sky-400' };
    case 'waiting_for_market':
      return { label: 'waiting', cls: 'text-muted-foreground/60' };
    case 'halted':
      return { label: queueReasonLabel(a.queueStatusReason), cls: 'text-red-400' };
    default:
      return { label: a.queueStatus || 'idle', cls: 'text-muted-foreground/50' };
  }
}

export function queueReasonLabel(reason?: string): string {
  switch (reason) {
    case 'no_depth': return 'no depth';
    case 'too_shallow': return 'too shallow';
    case 'unknown_queue': return 'unknown';
    case 'partial_depth': return 'partial';
    case 'book_guard': return 'book guard';
    case 'filled_inventory': return 'filled halt';
    case 'no_view': return 'no book';
    case 'safe_queue': return 'safe';
    case 'no_resting': return 'idle';
    default: return reason || 'idle';
  }
}

export function restingTotal(a: AgentStats): number {
  return (a.resting ?? []).reduce((s, o) => s + o.sizeContracts, 0);
}

// ── fills-vs-pulls FLOW: a SEPARATE market-quality channel (green fill-heavy →
//    red pull-heavy). NOT the queue-distance rainbow — this axis answers "is the
//    demand ahead of us real (fills) or fleeing (pulls)", the readout that makes
//    the pullToxicityRatioPct guard observable. ──────────────────────────────
/** Aggregate the fills-vs-pulls flow across a bot's resting legs. `null` until
 *  the edge emits the fields (every leg absent) → the UI renders "—". */
export function flowOf(a: AgentStats): { fills: number; pulls: number } | null {
  const legs = (a.resting ?? []).filter(
    (o) => o.fillsAheadContracts != null || o.pullsAheadContracts != null,
  );
  if (legs.length === 0) return null;
  let fills = 0;
  let pulls = 0;
  for (const o of legs) {
    fills += o.fillsAheadContracts ?? 0;
    pulls += o.pullsAheadContracts ?? 0;
  }
  return { fills, pulls };
}

/** Pull ratio from raw fill/pull counts: pulls / (fills + pulls) as a %. HIGH =
 *  pull-heavy (toxic); LOW = fill-heavy. -1 when zero flow observed. The one
 *  definition shared by the edge lane and the in-relay vanilla flow cell. */
export function pullRatioFrom(fills: number, pulls: number): number {
  const denom = fills + pulls;
  if (denom <= 0) return -1;
  return Math.round((pulls / denom) * 100);
}

/** Pull ratio = pulls / (fills + pulls) as a %. HIGH = pull-heavy (toxic, marks
 *  fleeing); LOW = fill-heavy (real demand). -1 when not derivable (edge silent
 *  or zero flow observed). */
export function pullRatioPct(a: AgentStats): number {
  const f = flowOf(a);
  if (!f) return -1;
  return pullRatioFrom(f.fills, f.pulls);
}

/** Tone for a pull-ratio %: fill-heavy green → mixed amber → pull-heavy red.
 *  Deliberately NOT heat() — market quality is its own channel (CLAUDE.md: don't
 *  reuse the queue-distance rainbow for a different axis). */
export function flowTone(pct: number): { text: string; label: string } {
  if (pct < 0) return { text: 'text-muted-foreground/40', label: 'fill vs pull flow — pending edge telemetry' };
  if (pct <= 33) return { text: 'text-green-400', label: 'fill-heavy — real demand ahead' };
  if (pct <= 66) return { text: 'text-amber-400', label: 'mixed flow ahead' };
  return { text: 'text-red-400', label: 'pull-heavy — marks fleeing (toxic)' };
}
export const fmtPullRatio = (p: number) => (p < 0 ? '—' : `${p}%`);

// ── recreational vs institutional: is the fill flow ahead of us big contracts
//    ("banks" = real demand worth resting in front of) or small ("marks" =
//    churn)? A SEPARATE axis again — combined with flow into the rest-quality
//    verdict below. Contract counts are Math.round'd (Kalshi volume is a float
//    — CLAUDE.md renderer rule). ────────────────────────────────────────────
/** Institutional / recreational fill split for a bot's market. `null` until the
 *  edge emits the counters. */
export function instRecOf(a: AgentStats): { inst: number; rec: number } | null {
  if (a.instFillContracts == null && a.recFillContracts == null) return null;
  return { inst: Math.round(a.instFillContracts ?? 0), rec: Math.round(a.recFillContracts ?? 0) };
}

/** Institutional share from raw counts: inst / (inst + rec) as a %. HIGH = banks
 *  buying; LOW = recreational churn. -1 when zero flow. Shared by the edge lane
 *  and the in-relay vanilla composite. */
export function instShareFrom(inst: number, rec: number): number {
  const denom = inst + rec;
  if (denom <= 0) return -1;
  return Math.round((inst / denom) * 100);
}

/** Institutional share = inst / (inst + rec) as a %. HIGH = banks buying (good
 *  to rest in front of); LOW = recreational churn. -1 when not derivable. */
export function instSharePct(a: AgentStats): number {
  const ir = instRecOf(a);
  if (!ir) return -1;
  return instShareFrom(ir.inst, ir.rec);
}

/** Round-and-abbreviate a contract count for display (Kalshi volume is a float). */
export const fmtContracts = (n: number): string => {
  const r = Math.round(n);
  const abs = Math.abs(r);
  if (abs >= 1000) return `${(r / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  return String(r);
};

/** Per-row badge: "inst buying" (banks) vs "rec churn" (marks) vs "mixed".
 *  `null` until the edge emits the counters → the row shows no badge. */
export function instRecBadge(a: AgentStats): { label: string; text: string } | null {
  const share = instSharePct(a);
  if (share < 0) return null;
  if (share >= 60) return { label: 'inst buying', text: 'text-sky-400' };
  if (share <= 40) return { label: 'rec churn', text: 'text-muted-foreground' };
  return { label: 'mixed', text: 'text-amber-400' };
}

/** The composite Josh wants: pulls-ahead LOW + institutional fills HIGH = a good
 *  market to rest in. Folds the flow (pull ratio) and inst/rec axes into ONE
 *  three-state verdict, degrading gracefully as each edge signal lands: a signal
 *  that's absent simply doesn't vote. All-absent → 'na' ("—"). */
export type RestState = 'good' | 'neutral' | 'toxic' | 'na';

/** The pure verdict: fold the pull-ratio (low = good) and inst-share (high =
 *  good) percentages into one three-state call. Each axis votes only when >= 0
 *  (a -1/absent signal abstains); all-absent -> 'na'. Shared by the edge lane
 *  and the in-relay vanilla composite. */
export function restVerdict(pullPct: number, instPct: number): { state: RestState; label: string; text: string } {
  const votes: ('good' | 'bad' | 'neutral')[] = [];
  if (pullPct >= 0) votes.push(pullPct <= 33 ? 'good' : pullPct >= 67 ? 'bad' : 'neutral');
  if (instPct >= 0) votes.push(instPct >= 60 ? 'good' : instPct <= 33 ? 'bad' : 'neutral');
  if (votes.length === 0) return { state: 'na', label: '—', text: 'text-muted-foreground/40' };
  const good = votes.filter((v) => v === 'good').length;
  const bad = votes.filter((v) => v === 'bad').length;
  // 'toxic' needs a corroborating second axis — a lone bad vote (e.g. pull ratio
  // high while the inst axis abstains at -1) routes to 'neutral', not 'toxic'.
  if (bad > 0 && good === 0 && votes.length >= 2)
    return { state: 'toxic', label: 'toxic', text: 'text-red-400' };
  if (good > 0 && bad === 0) return { state: 'good', label: 'good to rest', text: 'text-green-400' };
  return { state: 'neutral', label: 'neutral', text: 'text-amber-400' };
}

export function restQuality(a: AgentStats): { state: RestState; label: string; text: string; detail: string } {
  const pull = pullRatioPct(a); // low = good
  const inst = instSharePct(a); // high = good
  const v = restVerdict(pull, inst);
  const parts: string[] = [];
  const ir = instRecOf(a);
  if (ir) parts.push(`inst ${fmtContracts(ir.inst)} · rec ${fmtContracts(ir.rec)}`);
  if (inst >= 0) parts.push(`inst share ${inst}%`);
  if (pull >= 0) parts.push(`pulls ${pull}%`);
  const detail = v.state === 'na' ? 'rest quality — pending edge telemetry' : parts.join(' · ');
  return { ...v, detail };
}
export function progressPct(front: number): number {
  if (front < 0) return 0;
  return Math.max(2, Math.min(100, Math.round((1 - Math.min(front, QUEUE_SCALE) / QUEUE_SCALE) * 100)));
}
export function legsOf(a: AgentStats): { side: 'bid' | 'ask'; size: number; price: number }[] {
  const r = a.resting ?? [];
  const out: { side: 'bid' | 'ask'; size: number; price: number }[] = [];
  for (const side of ['bid', 'ask'] as const) {
    const os = r.filter((o) => o.side === side);
    if (os.length) out.push({ side, size: os.reduce((x, o) => x + o.sizeContracts, 0), price: os[0].priceCents });
  }
  return out;
}
export function uptime(startedAtMs: number): string {
  if (!startedAtMs) return '—';
  const s = Math.max(0, Math.round((Date.now() - startedAtMs) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60)}m`;
}

export function deriveMotion(hist: Sample[] | undefined): Motion {
  const all = hist ?? [];
  const valid = all.filter((s) => s.front >= 0);
  const spark = valid.map((s) => Math.max(0, QUEUE_SCALE - Math.min(s.front, QUEUE_SCALE))); // rising = climbing
  // climb/ETA over ONLY the trailing contiguous run of known-front samples, so a
  // re-quote (front → unknown(-1) → new front) isn't bridged into a bogus
  // derivative. MM bots cancel/replace constantly, making that gap common; a
  // bridged climb would over-state how fast the line is nearing the front (and
  // under-state the fill-ETA). < 2 contiguous samples ⇒ no climb yet (honest "—").
  const run: Sample[] = [];
  for (let i = all.length - 1; i >= 0 && all[i].front >= 0; i--) run.unshift(all[i]);
  // Proximity is meaningful from the first sample of the run (front at/near
  // placement); it needs only ONE known sample, unlike climb which needs two.
  const prox = run.length >= 1 && run[0].front > 0
    ? Math.round((run[run.length - 1].front / run[0].front) * 100)
    : -1;
  if (run.length < 2) return { climb: 0, eta: -1, prox, spark };
  const a = run[0];
  const b = run[run.length - 1];
  const dt = (b.ms - a.ms) / 1000;
  const climb = dt > 0 ? (a.front - b.front) / dt : 0; // contracts/sec advancing toward the front
  const eta = climb > 0.02 && b.front >= 0 ? b.front / climb : -1;
  return { climb, eta, prox, spark };
}

export const fmtClimb = (c: number) => (Math.abs(c) < 0.05 ? '0/s' : `${c > 0 ? '+' : ''}${c.toFixed(1)}/s`);
export const fmtEta = (e: number) => (e < 0 ? '—' : e < 90 ? `~${Math.round(e)}s` : `~${Math.round(e / 60)}m`);
export const fmtProx = (p: number) => (p < 0 ? '—' : `${p}%`);
export const fmtVel = (v?: number) => (v == null ? '—' : `${v.toFixed(1)}¢/s`);
export const fmtNet = (n: number) => `${n > 0 ? '+' : ''}${n}`;
export function markout5s(a: AgentStats): string {
  const m = (a.markout ?? []).find((x) => x.horizonMs === 5000);
  return m && m.fills > 0 ? `${Math.round(m.rightSideRate * 100)}%` : '—';
}
